import { MIME_TYPE_JSON_LD } from "@/lib/constants/mimeTypes";
import { getConfigValue } from "@rumble-it/common-lib/src/config";
import { User } from "oidc-client-ts";

/* Inspiration for this file taken from: https://github.com/api-platform/demo/blob/main/pwa */
type RequestCache =
  | "default"
  | "force-cache"
  | "no-cache"
  | "no-store"
  | "only-if-cached"
  | "reload";
type RequestParams = {
  headers: any;
  method?: string;
  body?: any;
  cache?: RequestCache;
};

type Config = {
  accept?: string;
  contentType?: string;
  method?: string;
  body?: any;
  cache?: RequestCache;
};

export const fetchData = async (endpoint: string, config: Config) => {
  try {
    const oidcStorage = sessionStorage.getItem(
      `oidc.user:${getConfigValue("authUrl")}/realms/${getConfigValue("authRealm")}:${getConfigValue("authClientId")}`,
    );

    if (!oidcStorage) {
      throw new Error("OIDC user not found in session storage.");
    }

    const user = User.fromStorageString(oidcStorage);

    const affiliateStorage = sessionStorage.getItem(
      `api.user.affiliate:${user?.profile?.sub}`,
    );

    if (!affiliateStorage) {
      throw new Error("Affiliate ID not found in session storage.");
    }

    const affiliateId = affiliateStorage;

    const accessToken = user?.access_token || null;

    const headers = {
      Accept: config?.accept ? config.accept : MIME_TYPE_JSON_LD,
      "Content-Type": config?.contentType
        ? config.contentType
        : MIME_TYPE_JSON_LD,
      Authorization: `Bearer ${accessToken}`,
    };

    const reqParams: RequestParams = {
      headers,
    };

    if (config?.method) reqParams.method = config.method;

    if (config?.body) reqParams.body = JSON.stringify(config.body);

    // If `config.cache` is set, use it, otherwise set it to `no-cache`.
    // The term `no-cache` can be confusing, as it does not mean "do not cache".
    // It means that the fetcher/browser should validate the cache with the server before using it.
    reqParams.cache = config?.cache || "no-cache";

    const reqUrlEndpoint = `${endpoint.startsWith("/") ? endpoint : "/" + endpoint}`;
    const reqUrlAffiliate = affiliateId ? `/affiliates/${affiliateId}` : "";
    const requestUrl = `${getConfigValue("apiUrl")}${reqUrlAffiliate}${reqUrlEndpoint}`;

    const res = await fetch(requestUrl, { ...reqParams });

    // console.debug(
    //   `🖧  ${reqParams.method ?? "GET"} to ${res.url} gave ${res.status}: ${res.statusText}`,
    // );

    if (res.status === 401) {
      window.location.href = "/login";
    }

    // In case of 204 No Content response
    if (res.status === 204) return;

    // If the response is a 404, throw an error
    if (res.status === 404) {
      throw new Error("404 (Not found)");
    }

    // If the response is a 422, throw an error
    if (res.status === 422) {
      throw new Error("422 (Unprocessable Entity)");
    }

    if (res.ok) {
      let data = await res.json();

      // If the response is a collection, extract the data
      if (data["@type"] === "Collection" && Array.isArray(data["member"])) {
        data = data["member"];
      }

      return {
        data,
      };
    }
  } catch (error) {
    console.error("fetchData: Error fetching data: ", error);
    throw new Error("fetchData: Failed to fetch data", error as ErrorOptions);
  }
};
